import {
  Primitive,
  z,
  ZodDiscriminatedUnion,
  ZodDiscriminatedUnionOption,
  ZodLiteral,
} from "zod";
import { unreachable } from "~/components/helpers";
import {
  currency,
  dispatchPurpose as dispatchPurposeEnum,
  expensesCategory,
  expensesType,
  incomeCategory,
  incomeType,
  paymentType,
} from "~/mydata";
import { expectAssignable } from "~/mydata/typeutils";
import {
  posReceipt,
  retailSalesReceipt,
  retailServicesReceipt,
  salesInvoice,
  salesInvoiceEU,
  salesInvoiceNonEU,
  salesInvoiceSelfPricing,
  servicesInvoice,
  servicesInvoiceEU,
  servicesInvoiceNonEU
} from "~/onesign/invoice";
import { amount } from "./amount";
import { exchangeRate } from "./exchange-rate";
import {
  FeeWithVAT,
  GiftCard,
  InvoiceItemType,
  isInvoiceItemType,
  isPositiveAmount, isZeroAmount, isZeroOrPositiveAmount,
  itemFeeWithVAT,
  itemGiftCard,
  itemOtherTaxWithVAT,
  lineItem,
  LineItem,
  OtherTaxWithVAT
} from "./invoice-item";

import { recipientEU, recipientGR, recipientNonEU } from "./recipient";
import { timestamp } from "./timestamp";
import { greekVATNumber } from "~/schemas/greek-vat-number";

export const retailCustomer = z.object({
  name: z.string().optional(),
  vatNumber: z.string().optional(),
  notes: z.string().optional(),
});
export const dispatchPurpose = z.nativeEnum(dispatchPurposeEnum);
export const invoiceDispatch = z.object({
  purpose: dispatchPurpose,
  vehicleNumber: z.string().optional(),
  locationAddress: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (val) return true;

        return false;
      },
      {
        message:
          "Η διεύθυνση πρέπει να έχει την μορφή Οδός, Αριθμός, ΤΚ, Πόλη, Χώρα",
      }
    ),
  sentAt: timestamp.optional().refine(
    (val) => {
      if (!val) return false;
      const selectedDate = new Date(val);
      const now = new Date();
      if (new Date(selectedDate.toDateString()) >= new Date(now.toDateString()))
        return true;

      return false;
    },
    {
      message: "Η ημερομηνία δεν μπορεί να είναι πριν την σημερινή",
    }
  ),
  dispatchTime: z.string()
});

export const payment = z.object({
  type: z.nativeEnum(paymentType),
  amount: amount.refine(
    isZeroOrPositiveAmount,
    "Το ποσό πρέπει να είναι μεγαλύτερο του μηδέν"
  ),
  info: z.string().optional(),
  tipAmount: z.string().optional(),
  transactionId: z.string().optional(),
  providerSignature: z.string().optional(),
  paymentMethodInfo: z.string().optional(),
});

export const invoiceCommonShape = {
  taxSeriesId: z.string().uuid(),
  aa: z.string(),
  currency: z.enum(currency).default("EUR"),
  exchangeRate: exchangeRate.optional(),
  emailAddress: z.string().optional(),
  payments: z
    .array(payment)
    .min(1, "Απαιτείται τουλάχιστον ένας τρόπος πληρωμής"),
};

export const supportedInvoices = {
  [salesInvoice]: z.object({
    invoiceType: z.literal(salesInvoice),
    recipient: recipientGR,
    items: z
      .array(
        z.discriminatedUnion("itemType", [
          lineItem[salesInvoice],
          itemOtherTaxWithVAT,
          itemFeeWithVAT,
          itemGiftCard,
        ]),
        {
          required_error: "Δεν έχουν προστεθεί γραμμές παραστατικού",
        }
      )
      .min(1, "Δεν έχουν προστεθεί γραμμές παραστατικού"),
    dispatch: invoiceDispatch.optional(),
    ...invoiceCommonShape,
  }),
  [salesInvoiceSelfPricing]: z.object({
    invoiceType: z.literal(salesInvoiceSelfPricing),
    ...invoiceCommonShape,
    recipient: recipientGR,
    items: z
      .array(
        z.discriminatedUnion("itemType", [
          lineItem[salesInvoiceSelfPricing],
          itemOtherTaxWithVAT,
          itemFeeWithVAT,
        ])
      )
      .min(1),
  }),
  [salesInvoiceEU]: z.object({
    invoiceType: z.literal(salesInvoiceEU),
    recipient: recipientEU,
    items: z.array(lineItem[salesInvoiceEU]).min(1),
    dispatch: invoiceDispatch.optional(),
    ...invoiceCommonShape,
  }),
  [salesInvoiceNonEU]: z.object({
    invoiceType: z.literal(salesInvoiceNonEU),
    recipient: recipientNonEU,
    items: z.array(lineItem[salesInvoiceNonEU]).min(1),
    dispatch: invoiceDispatch.optional(),
    ...invoiceCommonShape,
  }),
  [servicesInvoice]: z.object({
    invoiceType: z.literal(servicesInvoice),
    recipient: recipientGR,
    items: z.array(lineItem[servicesInvoice]).min(1),
    dispatch: invoiceDispatch.optional(),
    ...invoiceCommonShape,
  }),
  [servicesInvoiceEU]: z.object({
    invoiceType: z.literal(servicesInvoiceEU),
    recipient: recipientEU,
    items: z.array(lineItem[servicesInvoiceEU]).min(1),
    dispatch: invoiceDispatch.optional(),
    ...invoiceCommonShape,
  }),
  [servicesInvoiceNonEU]: z.object({
    invoiceType: z.literal(servicesInvoiceNonEU),
    recipient: recipientNonEU,
    items: z.array(lineItem[servicesInvoiceNonEU]).min(1),
    dispatch: invoiceDispatch.optional(),
    ...invoiceCommonShape,
  }),
  [retailSalesReceipt]: z.object({
    invoiceType: z.literal(retailSalesReceipt),
    retailCustomer: retailCustomer.optional(),
    items: z
      .array(
        z.discriminatedUnion("itemType", [
          lineItem[retailSalesReceipt],
          itemOtherTaxWithVAT,
          itemFeeWithVAT,
        ])
      )
      .min(1),
    ...invoiceCommonShape,
  }),
  [retailServicesReceipt]: z.object({
    invoiceType: z.literal(retailServicesReceipt),
    retailCustomer: retailCustomer.optional(),
    items: z
      .array(
        z.discriminatedUnion("itemType", [
          lineItem[retailServicesReceipt],
          itemOtherTaxWithVAT,
          itemFeeWithVAT,
        ])
      )
      .min(1),
    ...invoiceCommonShape,
  }),
  [posReceipt]: z.object({
    invoiceType: z.literal(posReceipt),
    recipient: recipientGR,
    items: z
      .array(
        z.discriminatedUnion("itemType", [
          lineItem[posReceipt],
        ]),
        {
          required_error: "Δεν έχουν προστεθεί γραμμές παραστατικού",
        }
      )
      .min(1, "Δεν έχουν προστεθεί γραμμές παραστατικού"),
    dispatch: invoiceDispatch.optional(),
    ...invoiceCommonShape,
  }),
} as const;

export const supportedInvoiceTypes = [
  salesInvoice,
  salesInvoiceSelfPricing,
  salesInvoiceEU,
  salesInvoiceNonEU,
  servicesInvoice,
  servicesInvoiceEU,
  servicesInvoiceNonEU,
  retailSalesReceipt,
  retailServicesReceipt,
  posReceipt,
] as const;

// Ensure all supported invoices are included in supportedInvoiceTypes
expectAssignable<Record<(typeof supportedInvoiceTypes)[number], any>>(
  supportedInvoices
);
expectAssignable<keyof typeof supportedInvoices>(
  supportedInvoiceTypes.find((_) => true)!
);

export const invoiceSchema = z.discriminatedUnion("invoiceType", [
  supportedInvoices[salesInvoice],
  supportedInvoices[salesInvoiceSelfPricing],
  supportedInvoices[salesInvoiceEU],
  supportedInvoices[salesInvoiceNonEU],
  supportedInvoices[servicesInvoice],
  supportedInvoices[servicesInvoiceEU],
  supportedInvoices[servicesInvoiceNonEU],
  supportedInvoices[retailSalesReceipt],
  supportedInvoices[retailServicesReceipt],
  supportedInvoices[posReceipt],
]);

export const incomeTotal = z.object({
  category: z.enum(incomeCategory),
  type: z.enum(incomeType),
  amount: amount,
});

export const expensesTotal = z.object({
  category: z.enum(expensesCategory),
  type: z.enum(expensesType),
  amount: amount,
});

export const invoiceTotals = z.object({
  totalNetValue: amount,
  totalVAT: amount,
  totalWithheldTax: amount,
  totalOtherTax: amount,
  totalFee: amount,
  totalStampDuty: amount,
  totalDeductions: amount,
  totalGrossValue: amount,
  totalIncome: z.array(incomeTotal),
  totalExpenses: z.array(expensesTotal),
});

export type InvoiceSchema = typeof invoiceSchema;
export type SupportedInvoiceType = DiscriminatorValues<InvoiceSchema>;

export const isSupportedInvoiceType = (
  input: unknown
): input is SupportedInvoiceType => {
  return typeof input === "string" && invoiceSchema.optionsMap.has(input);
};
expectAssignable<{ [T in SupportedInvoiceType]: InvoiceSchemaVariant<T> }>(
  supportedInvoices
);

export type InvoiceSchemaVariant<T extends SupportedInvoiceType> =
  DiscriminatedUnionOptions<InvoiceSchema, T>;

export type InvoiceItemSchemaVariant<
  T extends SupportedInvoiceType,
  I extends InvoiceItemType
> = DiscriminatedUnionOptions<
  InvoiceSchemaVariant<T>["shape"]["items"]["element"],
  I
>;
export type InvoiceItemSchema = InvoiceItemSchemaVariant<
  SupportedInvoiceType,
  InvoiceItemType
>;

export function resolveInvoiceSchema(invoiceType: SupportedInvoiceType) {
  return invoiceSchema.optionsMap.get(invoiceType)!;
}

export const resolveInvoiceItemSchema = <
  T extends SupportedInvoiceType,
  I extends InvoiceItemType
>(
  invoiceType: T,
  itemType: I
) => {
  if (itemType === LineItem) {
    return lineItem[invoiceType];
  } else if (itemType === GiftCard) {
    return itemGiftCard;
  } else if (itemType === OtherTaxWithVAT) {
    return itemOtherTaxWithVAT;
  } else if (itemType === FeeWithVAT) {
    return itemFeeWithVAT;
  } else {
    return unreachable(itemType);
  }
};

export function resolveInvoiceItemTypes(invoiceType: SupportedInvoiceType) {
  const itemSchema = supportedInvoices[invoiceType].shape.items.element;
  return [
    itemSchema instanceof ZodDiscriminatedUnion
      ? Array.from(itemSchema.optionsMap.keys()).map((v) => v?.toString())
      : itemSchema.shape.itemType.value.toString(),
  ].filter(isInvoiceItemType);
}

export type DiscriminatorValues<T> = T extends ZodDiscriminatedUnion<
  infer Discriminator,
  infer Options
>
  ? Options[number] extends ZodDiscriminatedUnionOption<Discriminator>
    ? Options[number]["shape"][Discriminator] extends ZodLiteral<infer Value>
      ? Value
      : "not_literal"
    : "not_d_option"
  : "not_d_union";

export type DiscriminatedUnionOptions<
  T,
  V extends Primitive
> = T extends ZodDiscriminatedUnion<infer Key, infer Options>
  ? Options extends Array<infer Option>
    ? Option extends ZodDiscriminatedUnionOption<Key>
      ? Option["shape"][Key] extends ZodLiteral<V>
        ? Option
        : never
      : never
    : never
  : never;

export type SupportedInvoice = z.output<InvoiceSchema>;
